/**
 * Client-side error reporter.
 *
 * Intercepts failed axios requests (4xx/5xx) AND uncaught window errors,
 * then sends a compact report to the backend which emails the admin.
 *
 * Strategy:
 * - Debounce by signature: same (url+status+message) is not re-sent within 5 minutes.
 * - Skip reporting for expected errors (401 on /auth/me, 404 lookups, etc.).
 * - Never throw from the reporter itself (fire-and-forget).
 */

import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const DEDUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const recentSignatures = new Map(); // signature -> timestamp

// URLs we don't want to alert about (expected failures)
const IGNORE_URL_PATTERNS = [
  /\/auth\/me$/,               // probing session = expected 401
  /\/client-errors$/,          // don't loop on the reporter itself
];

// Status codes we don't care about
const IGNORE_STATUSES = new Set([401]); // unauthenticated is noise

const isIgnored = (url, status) => {
  if (IGNORE_STATUSES.has(status)) return true;
  if (!url) return false;
  return IGNORE_URL_PATTERNS.some((rx) => rx.test(url));
};

const shouldReport = (signature) => {
  const now = Date.now();
  const last = recentSignatures.get(signature);
  if (last && now - last < DEDUP_WINDOW_MS) return false;
  recentSignatures.set(signature, now);
  // Best-effort cleanup so the Map doesn't grow forever
  if (recentSignatures.size > 200) {
    const cutoff = now - DEDUP_WINDOW_MS;
    for (const [k, t] of recentSignatures) {
      if (t < cutoff) recentSignatures.delete(k);
    }
  }
  return true;
};

const sendReport = async (payload) => {
  try {
    // Use fetch + keepalive so it survives a navigation/unload
    await fetch(`${API_URL}/client-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Silent: reporter must never break the app
  }
};

const currentUserInfo = () => {
  try {
    const token = localStorage.getItem("doctodays_token");
    const practitionerToken = localStorage.getItem("practitioner_token");
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) return "admin";
    if (practitionerToken) return "practitioner";
    if (token) return "patient";
    return "anonymous";
  } catch {
    return "unknown";
  }
};

export const setupErrorReporter = () => {
  // --- Axios interceptor: capture all 4xx/5xx ---
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const status = error?.response?.status || 0;
        const url = error?.config?.url || "";
        const method = (error?.config?.method || "GET").toUpperCase();

        if (!isIgnored(url, status)) {
          const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error?.message ||
            "Unknown error";

          const signature = `axios:${method}:${url}:${status}:${String(message).slice(0, 80)}`;
          if (shouldReport(signature)) {
            sendReport({
              kind: "axios",
              url,
              method,
              status,
              message: typeof message === "string" ? message.slice(0, 500) : JSON.stringify(message).slice(0, 500),
              page: window.location.pathname + window.location.search,
              role: currentUserInfo(),
              user_agent: navigator.userAgent,
            });
          }
        }
      } catch {
        // swallow
      }
      return Promise.reject(error);
    }
  );

  // --- Uncaught JS errors ---
  window.addEventListener("error", (event) => {
    try {
      const message = event?.message || "Uncaught error";
      const src = event?.filename || "unknown";
      const signature = `js:${src}:${message}`.slice(0, 160);
      if (shouldReport(signature)) {
        sendReport({
          kind: "js",
          url: src,
          method: "",
          status: 0,
          message: `${message} @ ${src}:${event?.lineno || 0}:${event?.colno || 0}`,
          page: window.location.pathname + window.location.search,
          role: currentUserInfo(),
          user_agent: navigator.userAgent,
        });
      }
    } catch {
      // swallow
    }
  });

  // --- Unhandled promise rejections ---
  window.addEventListener("unhandledrejection", (event) => {
    try {
      const reason = event?.reason;
      // If it's already an axios error, the axios interceptor will report it
      if (reason && reason.isAxiosError) return;
      const message =
        (reason && (reason.message || String(reason))) || "Unhandled rejection";
      const signature = `unhandled:${message}`.slice(0, 160);
      if (shouldReport(signature)) {
        sendReport({
          kind: "unhandled",
          url: "",
          method: "",
          status: 0,
          message: String(message).slice(0, 500),
          page: window.location.pathname + window.location.search,
          role: currentUserInfo(),
          user_agent: navigator.userAgent,
        });
      }
    } catch {
      // swallow
    }
  });
};
